import { useEffect } from 'react';
import { useActiveOrgStore, useAuthStore } from '@store/index';
import { applyLocale } from '../i18n/localePreference';

/**
 * REV-798: apply the signed-in person's stored language preference.
 *
 * The user's own `locale` wins, then the active organization's, as on the API
 * (revenant-api utils/locale.js). With neither, nothing is applied and the
 * language the browser detector chose stays. Mounted once, at the app root, so
 * it runs on sign-in, on page reload (the auth store is persisted) and whenever
 * the preference changes in Settings or the language menu, which is what makes a
 * change take effect without signing in again.
 *
 * Signed out, nothing is applied: a preference belongs to a signed-in person.
 */
export function useLocalePreference(): void {
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const userLocale = useAuthStore((s) => s.user?.locale);
  const orgLocale = useActiveOrgStore((s) => s.activeOrg?.locale);
  const preferred = userLocale ?? orgLocale ?? null;

  useEffect(() => {
    if (signedIn && preferred) {
      void applyLocale(preferred);
    }
  }, [signedIn, preferred]);
}
